import { OrganizationPlaceholderIllustration } from '@carboncopyco/illustrations';
import classNames from 'classnames';
import type React from 'react';
import type { Colors } from '../../constants';
import { Image } from '../Image';
import {
  TextBadge,
  TextCaption,
  TextHero,
  TextSectionHeader,
} from '../Typography';

export type AvatarSize =
  | 'xxlarge'
  | 'xlarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'xsmall';

export type AvatarShape = 'circular' | 'squared';

export const avatarSizes: Record<AvatarSize, string> = {
  xxlarge: 'w-ds-34 h-ds-34',
  xlarge: 'w-ds-17 h-ds-17',
  large: 'w-ds-12 h-ds-12',
  medium: 'w-ds-7.5 h-ds-7.5',
  small: 'w-ds-6 h-ds-6',
  xsmall: 'w-ds-4 h-ds-4',
};

export const squaredRadiuses: Record<AvatarSize, string> = {
  xxlarge: 'rounded-ds-sm',
  xlarge: 'rounded-ds-sm',
  large: 'rounded-ds-sm',
  medium: 'rounded-ds-sm',
  small: 'rounded-ds-sm',
  xsmall: 'rounded-ds-sm',
};

const textComponents: Record<
  AvatarSize,
  React.ComponentType<{
    uppercase: boolean;
    color: Colors;
    label: string;
  }>
> = {
  xxlarge: TextHero,
  xlarge: TextSectionHeader,
  large: TextSectionHeader,
  medium: TextCaption,
  small: TextCaption,
  xsmall: TextBadge,
};

export interface AvatarProps {
  initials?: string;
  image?: string;
  backgroundColor?: string;
  color?: Colors;
  size?: AvatarSize;
  shape?: AvatarShape;
}

export const Avatar = ({
  initials = 'DS',
  image,
  color = 'text-ds-accent3-100',
  backgroundColor = 'bg-ds-secondary-400',
  size = 'medium',
  shape = 'circular',
}: AvatarProps) => {
  const TextComponent = textComponents[size];

  return (
    <div
      className={classNames(
        'flex items-center justify-center',
        avatarSizes[size],
        shape === 'circular' ? 'rounded-full' : squaredRadiuses[size],
        image
          ? 'border border-ds-natural-150'
          : !initials && 'border border-ds-natural-100',
        !image && backgroundColor
      )}
    >
      {image ? (
        <Image
          src={image}
          circle={shape === 'circular'}
          background
        />
      ) : initials ? (
        <TextComponent
          uppercase
          label={initials}
          color={color}
        />
      ) : (
        <OrganizationPlaceholderIllustration />
      )}
    </div>
  );
};
